import { useCallback, useMemo } from 'react';

import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { SecondaryButtonLink } from 'approot/shared/styles/buttons/secondary-button.style';
import {
  NotificationActions,
  NotificationContent,
  NotificationTip,
  NotificationTipText,
} from 'approot/shared/notifications/notification-tip.style';
import { ImageProperties, Props } from './image.constants';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import {
  ASSESSMENT,
  IMAGE,
  LESSON_ACTIVITIES,
  LESSON,
  DRAFT_ACTION_COMPONENT_IMAGE_UPDATE,
} from 'global/constants';
import { generateStimulusCreateLink } from 'approot/activity-builder/components/shared/stimulus-library';
import { getInteractiveContentLabel } from 'approot/activity-builder/activity-builder.utils';
import Switch from 'approot/shared/switch/switch';
import ComponentImageField from 'approot/activity-builder/components/shared/component-image-field/component-image-field';
import ComponentImageModal from 'approot/activity-builder/components/shared/component-image-modal/component-image-modal';
import { getBaseImagePayload } from './image.utils';
import { useFindComponentDimensions } from '../component.hooks';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const ImageEditForm = ({
  id,
  sectionId,
  contentId,
  lessonId,
  properties,
}: Props) => {
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { closeDrawer } = useActivityBuilderContext();

  const { slug, contentType } = useGetInteractiveLessonParams();
  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();

  const { elementRef } = useFindComponentDimensions(id);

  const handleUpdate = useCallback(
    (properties: ImageProperties) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: getBaseImagePayload(properties),
          },
        },
        properties,
        typename: 'ActivitiesImageComponent',
      });
    },
    [id, sectionId, updateComponentProperties]
  );

  const contentWording = useMemo(
    () => getInteractiveContentLabel(contentType).toLowerCase(),
    [contentType]
  );

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader title="Image" onClose={closeDrawer} />
      {isVisible && elementRef && (
        <ComponentImageModal
          loading={loading}
          containerDimensions={{
            width: elementRef.offsetWidth,
            height: elementRef.offsetHeight,
          }}
          image={properties.image || undefined}
          sourceUrl={properties.sourceUrl}
          altText={properties.altText}
          cropData={properties.cropData}
          closeModal={closeModal}
          onUpdateData={data => {
            const image = data.image;
            const cropData = data.cropData;
            updateComponentProperties({
              input: {
                sectionId,
                componentId: id,
                componentData: {
                  properties: {
                    sourceUrl: data.sourceUrl,
                    ...getBaseImagePayload({
                      ...properties,
                      cropData: cropData
                        ? {
                            ...cropData,
                            __typename: 'CropData',
                          }
                        : null,
                    }),
                    altText: data.altText,
                    image: image
                      ? {
                          fileName: image.fileName,
                          key: image.key,
                          imageType: 'content',
                          data: {
                            flip: image.data?.flip || null,
                          },
                        }
                      : null,
                  },
                },
                addDraftAction: DRAFT_ACTION_COMPONENT_IMAGE_UPDATE,
              },
              typename: 'ActivitiesImageComponent',
              isSaveImmediate: true,
              onCompleted: () => {
                closeModal();
              },
            });
          }}
        />
      )}
      <ActivityBuilderDrawerContent>
        <FileDropperContextProvider>
          <SettingsGroup>
            <SettingsLabel>Image Upload</SettingsLabel>
            <ComponentImageField
              image={
                properties.image
                  ? {
                      key: properties.image.key,
                      fileName: properties.image.fileName,
                      original: properties.image.original,
                      flip: properties.image.data?.flip,
                      alt: properties.altText,
                    }
                  : null
              }
              ariaLabel="Edit Image"
              onEdit={openModal}
            />
          </SettingsGroup>
          <SettingsGroup>
            <SettingsLabel>Image Caption</SettingsLabel>
            <Switch
              data-dropdown-item="true"
              ariaLabel={`Click to ${
                properties.showCaptionOnLesson ? 'hide' : 'show'
              } caption`}
              state={properties.showCaptionOnLesson}
              onClick={() => {
                handleUpdate({
                  ...properties,
                  showCaptionOnLesson: !properties.showCaptionOnLesson,
                });
              }}
            />
          </SettingsGroup>
        </FileDropperContextProvider>

        <NotificationTip $marginBottom={24}>
          <NotificationContent>
            <NotificationTipText>
              Save this image to the stimulus library so teachers can find it
              and creators can easily add it to other {contentWording}s. Images
              that are not in the stimulus library will only be viewed in the{' '}
              {contentWording}.
            </NotificationTipText>
            <NotificationActions>
              <SecondaryButtonLink
                data-selector="btn-add-to-library"
                $isSmall
                to={generateStimulusCreateLink({
                  lessonType:
                    contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
                  activitiesContentId: lessonId,
                  sectionId,
                  contentId,
                  componentId: id,
                  type: IMAGE,
                  slug,
                })}
              >
                Add to Stimulus Library
              </SecondaryButtonLink>
            </NotificationActions>
          </NotificationContent>
        </NotificationTip>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default ImageEditForm;
